import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchUserAttributes,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
  updatePassword,
  confirmSignIn,
} from 'aws-amplify/auth';
import * as Sentry from '@sentry/react';
import { SESSION_EXPIRED_EVENT, resetSessionExpiredNotifier } from '../lib/api';

interface User {
  id: string;
  email: string;
  companyName?: string;
  contactName?: string;
  preferredLanguage?: 'en' | 'fr';
  isAdmin: boolean;
}

interface LoginResult {
  needsNewPassword: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  /** True when the user was signed out because their session expired mid-use
   *  (vs. an explicit logout) — lets the login page explain what happened. */
  sessionExpired: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPasswordConfirm: (email: string, code: string, newPassword: string) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  // api.ts fires this when auth is terminally dead (refresh token expired or
  // revoked). Clearing `user` makes ProtectedRoute redirect to /login, where
  // `sessionExpired` drives the "session expired — please sign in" notice.
  useEffect(() => {
    function onSessionExpired() {
      setSessionExpired(true);
      setUser(null);
      Sentry.setUser(null);
      // Best-effort: clear whatever stale Amplify state is left.
      signOut().catch(() => {});
    }
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  async function checkAuth() {
    try {
      const cognitoUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      const session = await fetchAuthSession();

      // Parse groups from the ID token
      let isAdmin = false;
      const idToken = session.tokens?.idToken;
      if (idToken) {
        const payload = idToken.payload;
        const groups = payload['cognito:groups'];
        if (Array.isArray(groups)) {
          isAdmin = groups.includes('admins');
        }
      }

      const userObj = {
        id: cognitoUser.userId,
        email: attributes.email || '',
        companyName: attributes['custom:companyName'],
        contactName: attributes['custom:contactName'],
        preferredLanguage: attributes['custom:preferredLanguage'] as 'en' | 'fr' | undefined,
        isAdmin,
      };
      setUser(userObj);
      Sentry.setUser({ id: userObj.id, email: userObj.email, username: userObj.companyName });
    } catch {
      setUser(null);
      Sentry.setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function login(email: string, password: string): Promise<LoginResult> {
    // Clear any stale Amplify auth session (e.g. from a previous failed attempt,
    // page refresh during NEW_PASSWORD_REQUIRED challenge, etc.)
    // Without this, signIn() throws and the user sees "Invalid credentials" even
    // though their password is correct.
    try { await signOut(); } catch { /* ignore — no session to clear */ }

    setSessionExpired(false);
    resetSessionExpiredNotifier();
    const result = await signIn({ username: email.trim().toLowerCase(), password });

    if (result.isSignedIn) {
      await checkAuth();
      return { needsNewPassword: false };
    } else if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return { needsNewPassword: true };
    }
    return { needsNewPassword: false };
  }

  async function completeNewPassword(newPassword: string) {
    const result = await confirmSignIn({ challengeResponse: newPassword });
    if (result.isSignedIn) {
      await checkAuth();
    }
  }

  async function logout() {
    await signOut();
    setUser(null);
    setSessionExpired(false);
    resetSessionExpiredNotifier();
  }

  async function forgotPassword(email: string) {
    await resetPassword({ username: email.trim().toLowerCase() });
  }

  async function resetPasswordConfirm(email: string, code: string, newPassword: string) {
    await confirmResetPassword({
      username: email.trim().toLowerCase(),
      confirmationCode: code,
      newPassword,
    });
  }

  async function changePassword(oldPassword: string, newPassword: string) {
    await updatePassword({ oldPassword, newPassword });
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        sessionExpired,
        login,
        completeNewPassword,
        logout,
        forgotPassword,
        resetPasswordConfirm,
        changePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
